import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Error } from "../../../../components/common/Error";
import { AdminActionCreator } from "../../../../store/Admin/actions";

import {
    selectIsAuth,
    selectSubscribedUsersPending,
    selectSubscribedUsersError,
    selectSubscribedUsersErrorMessage,
    selectSubscribedUsersList,
    selectSubscribedUsersTotal,
    selectSubscribedUsersPage,
    selectSubscribedUsersPerPage,
    selectSubscribedUsersLastPage,
    selectSubscribedUsersNextUrl,
    selectSubscribedUsersPrevUrl,
} from "../../../../store/selectors";

import { useFetchSubscribedUsers } from "../../hooks/useFetchSubscribedUsers";
import { useDecoratedUsers, type SortKey, type SortDir } from "../../hooks/useDecoratedUsers";

import { SearchBar } from "../SearchBar";
import { EmptyState } from "../EmptyState";
import { LoadingState } from "../LoadingState";
import { UsersTable } from "../UsersTable";

export const _SubscribedUsersPanel = ({
                                          isAuth,

                                          pending,
                                          error,
                                          errorMessage,

                                          users,
                                          total,
                                          page,
                                          perPage,
                                          lastPage,
                                          nextUrl,
                                          prevUrl,

                                          getSubscribedUsers,
                                      }: any) => {
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState<SortKey>("expiresAt");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const nextPageRequestRef = useRef(false);
    const nextPageSentinelRef = useRef<HTMLDivElement | null>(null);

    useFetchSubscribedUsers({
        isAuth,
        perPage,
        getSubscribedUsers,
    });

    const decoratedUsers = useDecoratedUsers({
        users,
        search,
        sortKey,
        sortDir,
    });

    const canLoadMore = !!nextUrl && !pending;

    const loadNextPage = useCallback(() => {
        if (!canLoadMore || nextPageRequestRef.current) return;
        nextPageRequestRef.current = true;
        getSubscribedUsers({ page: page + 1, perPage });
    }, [canLoadMore, getSubscribedUsers, page, perPage]);

    useEffect(() => {
        if (!pending) nextPageRequestRef.current = false;
    }, [pending]);

    useEffect(() => {
        const target = nextPageSentinelRef.current;
        if (!target || !canLoadMore) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) loadNextPage();
        }, { rootMargin: "320px 0px" });
        observer.observe(target);
        return () => observer.disconnect();
    }, [canLoadMore, loadNextPage]);

    if (!isAuth) {
        return <Error title="Требуется авторизация" description="Войдите в аккаунт, чтобы просматривать подписчиков." />;
    }

    if (pending && (users?.length ?? 0) === 0) {
        return <LoadingState title="Загрузка подписчиков..." />;
    }

    if (error && (users?.length ?? 0) === 0) {
        return <Error title="Не удалось загрузить подписчиков" description={errorMessage || "Попробуйте обновить страницу"} />;
    }

    return (
        <div className="w-full">
            <div className="mt-4">
                <SearchBar value={search} onChange={setSearch} />
            </div>

            <div className="mt-4">
                {decoratedUsers.length === 0 ? (
                    <EmptyState />
                ) : (
                    <UsersTable
                        variant="subscribers"
                        title="Подписчики"
                        users={decoratedUsers}
                        page={page}
                        lastPage={lastPage}
                        total={total}
                        pending={pending}
                        actionsDisabled={pending}
                        sortKey={sortKey as any}
                        sortDir={sortDir}
                        onChangeSortKey={setSortKey as any}
                        onToggleDir={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                    />
                )}
            </div>

            <div ref={nextPageSentinelRef} className="flex min-h-14 items-center justify-center py-5">
                {pending && decoratedUsers.length > 0 ? <span className="text-sm text-slate-500">Загружаем ещё подписчиков…</span> : null}
                {!pending && nextUrl ? <button type="button" onClick={loadNextPage} className="text-sm font-semibold text-[#8b2a5e] hover:text-[#6e2048]">Показать ещё</button> : null}
                {!pending && !nextUrl && decoratedUsers.length > 0 ? <span className="text-sm text-slate-400">Все подписчики загружены</span> : null}
            </div>

            {error && (users?.length ?? 0) > 0 ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Ошибка обновления: {errorMessage || "Unknown error"}
                </div>
            ) : null}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    isAuth: selectIsAuth(state),

    pending: selectSubscribedUsersPending(state),
    error: selectSubscribedUsersError(state),
    errorMessage: selectSubscribedUsersErrorMessage(state),

    users: selectSubscribedUsersList(state),
    total: selectSubscribedUsersTotal(state),

    page: selectSubscribedUsersPage(state),
    perPage: selectSubscribedUsersPerPage(state),
    lastPage: selectSubscribedUsersLastPage(state),

    nextUrl: selectSubscribedUsersNextUrl(state),
    prevUrl: selectSubscribedUsersPrevUrl(state),
});

const mapDispatchToProps = {
    getSubscribedUsers: AdminActionCreator.getSubscribedUsers,
};

export const SubscribedUsersPanel = connect(mapStateToProps, mapDispatchToProps)(_SubscribedUsersPanel);
